import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Search, ChevronUp, MessageCircle, Clock, AlertCircle, CheckCircle, X, LayoutDashboard } from 'lucide-react';
import Navbar from '../Components/Navbar';
import TutorialVideoSection from '../Components/videos/TutorialVideoSection';
import { Footer } from '../Components/Footer';
import { useTranslation } from '../../TranslationContext';
import TicketSystem from './TicketSystem';

const COLORS = {
  primary: '#c06a70',
  secondary: '#879bae',
  background: '#f5f5dc',
  darkText: '#333333',
  lightText: '#ffffff',
  border: '#e2e8f0',
  danger: '#f44336',
  success: '#4caf50',
};

interface FAQItem {
  question: string;
  answer: string;
  isOpen: boolean;
}

interface ModalState {
  isOpen: boolean;
  title: string;
  message: string;
  type: 'success' | 'error';
}

export const Support: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [hasCompany, setHasCompany] = useState(false);
  const [modal, setModal] = useState<ModalState>({
    isOpen: false,
    title: '',
    message: '',
    type: 'success',
  });
  const [faqItems, setFaqItems] = useState<FAQItem[]>([
    {
      question: t('faq_mfa_question'),
      answer: t('faq_mfa_answer'),
      isOpen: false,
    },
    {
      question: t('faq_api_question'),
      answer: t('faq_api_answer'),
      isOpen: false,
    },
    {
      question: t('faq_password_question'),
      answer: t('faq_password_answer'),
      isOpen: false,
    },
  ]);

  useEffect(() => {
    const company = localStorage.getItem('company');
    if (company !== null) {
      setHasCompany(true);
    }

    // Défilement vers la section des tickets si demandé
    if (location.state?.scrollToTickets) {
      const ticketSection = document.getElementById('ticket-system');
      if (ticketSection) {
        ticketSection.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location.state]);

  const closeModal = () => {
    setModal({ ...modal, isOpen: false });
  };

  const toggleFaqItem = (index: number) => {
    setFaqItems(
      faqItems.map((item, i) =>
        i === index ? { ...item, isOpen: !item.isOpen } : { ...item, isOpen: false }
      )
    );
  };

  // Fonction pour défiler vers la section des tickets
  const scrollToTicketSystem = () => {
    const ticketSection = document.getElementById('ticket-system');
    if (ticketSection) {
      ticketSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Fonction pour naviguer vers le dashboard
  const handleDashboardClick = () => {
    navigate('/EntrepriseDashboard');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-slate-800 to-slate-700 py-16">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 bg-[url('/pattern.svg')] opacity-5"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {t('support_center')}
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto mb-8">
            {t('support_center_description')}
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to="/faq"
              className="px-6 py-3 bg-#F3DAB3 text-white rounded-lg hover:bg-yellow-500 transition-colors duration-300 shadow-lg shadow-sky-500/20"
            >
              {t('view_full_faq')}
            </Link>
            {/* Afficher le bouton Live Chat uniquement si l'utilisateur a une company */}
            {hasCompany && (
              <button 
                onClick={scrollToTicketSystem}
                className="px-6 py-3 bg-rose-500 text-white rounded-lg hover:bg-rose-600 transition-colors duration-300 shadow-lg shadow-rose-500/20 flex items-center"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                {t('start_live_chat')}
              </button>
            )}
            {/* Afficher le bouton Dashboard uniquement si l'utilisateur a une company */}
            {hasCompany && (
              <button 
                onClick={handleDashboardClick}
                className="px-6 py-3 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors duration-300 shadow-lg shadow-slate-500/20 flex items-center"
              >
                <LayoutDashboard className="w-5 h-5 mr-2" />
                {t('admin_dashboard') || 'Dashboard'}
              </button>
            )}
            
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Status Indicator */}
        <div className="flex items-center justify-center mb-12 bg-white rounded-full py-3 px-6 shadow-md max-w-md mx-auto">
          <span className="h-3 w-3 bg-green-500 rounded-full mr-2 animate-pulse"></span>
          <p className="text-slate-600 flex items-center">
            <span className="font-medium mr-1 cliffs">{t('support_status')}</span> •
            <Clock className="w-4 h-4 mx-2 text-slate-400" />
            <span className="text-slate-500">{t('avg_response_time')}</span>
          </p>
        </div>

        {/* Quick Help Section */}
        <div className="bg-white rounded-xl shadow-md p-8 mb-12 border border-slate-200">
          <div className="flex items-center mb-6">
            <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center mr-4">
              <Search className="w-5 h-5 text-sky-500" />
            </div>
            <h2 className="text-2xl font-semibold text-slate-800">
              {t('frequently_asked_questions')}
            </h2>
          </div>

          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <div key={index} className="border-b border-slate-200 pb-4">
                <button
                  className="w-full text-left flex items-center justify-between py-3 focus:outline-none group"
                  onClick={() => toggleFaqItem(index)}
                >
                  <span className="text-lg font-medium text-slate-700 group-hover:text-sky-600 transition-colors duration-200">
                    {item.question}
                  </span>
                  <span className={`transform transition-transform duration-200 ${item.isOpen ? 'rotate-180' : ''}`}>
                    <ChevronUp className="w-5 h-5 text-slate-400" />
                  </span>
                </button>

                {item.isOpen && (
                  <div className="mt-2 pl-4 pr-2 pb-2 text-slate-600 bg-slate-50 rounded-lg p-4 animate-fadeIn">
                    {item.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Ticket System */}
        {hasCompany && (
          <div id="ticket-system" className="mb-12">
            <TicketSystem setModal={setModal} />
          </div>
        )}

        {/* Tutorials Section */}
        {hasCompany && (
          <div className="mb-12">
            <TutorialVideoSection />
          </div>
        )}

        {/* Contact Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          <div className="bg-white rounded-xl shadow-md p-8 border border-slate-200">
            <div className="flex items-center mb-6">
              <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center mr-4">
                <MessageCircle className="w-5 h-5 text-sky-500" />
              </div>
              <h2 className="text-2xl font-semibold text-slate-800">
                {t('contact_us_directly')}
              </h2>
            </div>
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg flex items-center">
                <div className="bg-white p-2 rounded-full mr-4 shadow-sm">
                  <svg className="w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-slate-500">{t('phone_support')}</p>
                  <p className="text-slate-700 font-medium">+237 .. .. .. ..</p>
                </div>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg flex items-center">
                <div className="bg-white p-2 rounded-full mr-4 shadow-sm">
                  <svg className="w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-slate-500">{t('email_support')}</p>
                  <p className="text-slate-700 font-medium">support@otenti.com</p>
                </div>
              </div>
              {/* Afficher les boutons uniquement si l'utilisateur a une company */}
              {hasCompany && (
                <div className="space-y-3">
                  <button
                    onClick={handleDashboardClick}
                    className="w-full bg-slate-600 hover:bg-slate-700 text-white py-3 px-6 rounded-lg transition-all duration-200 transform hover:-translate-y-1 shadow-lg shadow-slate-500/20 flex items-center justify-center"
                  >
                    <LayoutDashboard className="w-5 h-5 mr-2" />
                    {t('admin_dashboard') || 'Accéder au Dashboard'}
                  </button>
                  <button
                    onClick={scrollToTicketSystem}
                    className="w-full bg-rose-500 hover:bg-rose-600 text-white py-3 px-6 rounded-lg transition-all duration-200 transform hover:-translate-y-1 shadow-lg shadow-rose-500/20 flex items-center justify-center"
                  >
                    <MessageCircle className="w-5 h-5 mr-2" />
                    {t('start_live_chat_now')}
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="bg-gradient-to-br from-slate-800 to-slate-700 rounded-xl shadow-md p-8 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-sky-400 rounded-full -translate-y-1/4 translate-x-1/4 opacity-10"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-rose-400 rounded-full translate-y-1/4 -translate-x-1/4 opacity-10"></div>
            <div className="relative z-10">
              <h2 className="text-2xl font-semibold mb-6">{t('support_hours')}</h2>
              <div className="space-y-4 mb-8">
                <div className="flex justify-between">
                  <span>{t('monday_friday')}</span>
                  <span className="font-medium">{t('monday_friday_hours')}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t('saturday')}</span>
                  <span className="font-medium">{t('saturday_hours')}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t('sunday')}</span>
                  <span className="font-medium">{t('sunday_hours')}</span>
                </div>
              </div>
              <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
                <h3 className="text-lg font-medium mb-2">{t('emergency_support')}</h3>
                <p className="text-slate-300 mb-4">{t('emergency_support_description')}</p>
                <Link to="/ Huntington Beach " className="text-sky-300 hover:text-sky-200 flex items-center">
                  {t('learn_premium_support')}
                  <svg className="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Modal for Success/Error Messages */}
        {modal.isOpen && (
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 p-4"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 100,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onClick={closeModal}
          >
            <div
              className="bg-white rounded-xl shadow-2xl w-full max-w-md relative"
              onClick={(e) => e.stopPropagation()}
              style={{
                maxHeight: '90vh',
                overflowY: 'auto',
              }}
            >
              {/* Modal Header */}
              <div
                className="p-6 pb-0 flex justify-between items-center"
                style={{ color: COLORS.darkText }}
              >
                <h2 className="text-xl font-bold flex items-center">
                  {modal.type === 'success' ? (
                    <CheckCircle className="mr-2" size={24} style={{ color: COLORS.success }} />
                  ) : (
                    <AlertCircle className="mr-2" size={24} style={{ color: COLORS.danger }} />
                  )}
                  {modal.title}
                </h2>
                <button
                  onClick={closeModal}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                  aria-label={t('close')}
                >
                  <X size={24} />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6">
                <div
                  className="p-3 rounded-lg flex items-center"
                  style={{
                    backgroundColor: modal.type === 'success' ? `${COLORS.success}10` : `${COLORS.danger}10`,
                    color: modal.type === 'success' ? COLORS.success : COLORS.danger,
                  }}
                >
                  {modal.type === 'success' ? (
                    <CheckCircle className="mr-2" size={20} />
                  ) : (
                    <AlertCircle className="mr-2" size={20} />
                  )}
                  <p className="text-sm">{modal.message}</p>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="p-6 pt-0 flex justify-end">
                <button
                  onClick={closeModal}
                  className="px-4 py-2 bg-opacity-90 text-white rounded-lg transition-colors"
                  style={{ backgroundColor: COLORS.primary }}
                >
                  {t('close')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Back to Top Button */}
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-8 right-8 bg-sky-500 text-white p-3 rounded-full shadow-lg hover:bg-sky-600 transition-colors duration-300 z-50"
        >
          <ChevronUp className="h-5 w-5" />
        </button>

        <Footer />

        {/* Add animations keyframes */}
        <style>{`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-fadeIn {
            animation: fadeIn 0.3s ease-out forwards;
          }
        `}</style>
      </div>
    </div>
  );
};

export default Support;

// import React, { useState, useEffect } from 'react';
// import { Link, useLocation } from 'react-router-dom';
// import { Search, ChevronUp, MessageCircle, Clock, AlertCircle, CheckCircle, X } from 'lucide-react';
// import Navbar from '../Components/Navbar';
// import TutorialVideoSection from '../Components/videos/TutorialVideoSection';
// import { Footer } from '../Components/Footer';
// import { useTranslation } from '../../TranslationContext';
// import TicketSystem from './TicketSystem';

// const COLORS = {
//   primary: '#c06a70',
//   secondary: '#879bae',
//   background: '#f5f5dc',
//   darkText: '#333333',
//   lightText: '#ffffff',
//   border: '#e2e8f0',
//   danger: '#f44336',
//   success: '#4caf50',
// };

// interface FAQItem {
//   question: string;
//   answer: string;
//   isOpen: boolean;
// }

// interface ModalState {
//   isOpen: boolean;
//   title: string;
//   message: string;
//   type: 'success' | 'error';
// }

// export const Support: React.FC = () => {
//   const { t } = useTranslation();
//   const location = useLocation();
//   const [hasCompany, setHasCompany] = useState(false);
//   const [modal, setModal] = useState<ModalState>({
//     isOpen: false,
//     title: '',
//     message: '',
//     type: 'success',
//   });
//   const [faqItems, setFaqItems] = useState<FAQItem[]>([
//     {
//       question: t('faq_mfa_question'),
//       answer: t('faq_mfa_answer'),
//       isOpen: false,
//     },
//     {
//       question: t('faq_api_question'),
//       answer: t('faq_api_answer'),
//       isOpen: false,
//     },
//     {
//       question: t('faq_password_question'),
//       answer: t('faq_password_answer'),
//       isOpen: false,
//     },
//   ]);

//   useEffect(() => {
//     const company = localStorage.getItem('company');
//     if (company !== null) {
//       setHasCompany(true);
//     }

//     // Défilement vers la section des tickets si demandé
//     if (location.state?.scrollToTickets) {
//       const ticketSection = document.getElementById('ticket-system');
//       if (ticketSection) {
//         ticketSection.scrollIntoView({ behavior: 'smooth' });
//       }
//     }
//   }, [location.state]);

//   const closeModal = () => {
//     setModal({ ...modal, isOpen: false });
//   };

//   const toggleFaqItem = (index: number) => {
//     setFaqItems(
//       faqItems.map((item, i) =>
//         i === index ? { ...item, isOpen: !item.isOpen } : { ...item, isOpen: false }
//       )
//     );
//   };

//   // Fonction pour défiler vers la section des tickets
//   const scrollToTicketSystem = () => {
//     const ticketSection = document.getElementById('ticket-system');
//     if (ticketSection) {
//       ticketSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
//     }
//   };

//   return (
//     <div className="min-h-screen bg-slate-50">
//       <Navbar />

//       {/* Hero Section */}
//       <div className="relative bg-gradient-to-r from-slate-800 to-slate-700 py-16">
//         <div className="absolute inset-0 overflow-hidden">
//           <div className="absolute inset-0 bg-[url('/pattern.svg')] opacity-5"></div>
//         </div>
//         <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
//           <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
//             {t('support_center')}
//           </h1>
//           <p className="text-xl text-slate-300 max-w-2xl mx-auto mb-8">
//             {t('support_center_description')}
//           </p>
//           <div className="flex flex-wrap gap-4 justify-center">
//             <Link
//               to="/faq"
//               className="px-6 py-3 bg-#F3DAB3 text-white rounded-lg hover:bg-yellow-500 transition-colors duration-300 shadow-lg shadow-sky-500/20"
//             >
//               {t('view_full_faq')}
//             </Link>
//             {/* Afficher le bouton Live Chat uniquement si l'utilisateur a une company */}
//             {hasCompany && (
//               <button 
//                 onClick={scrollToTicketSystem}
//                 className="px-6 py-3 bg-rose-500 text-white rounded-lg hover:bg-rose-600 transition-colors duration-300 shadow-lg shadow-rose-500/20 flex items-center"
//               >
//                 <MessageCircle className="w-5 h-5 mr-2" />
//                 {t('start_live_chat')}
//               </button>
//             )}
//           </div>
//         </div>
//       </div>

//       <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
//         {/* Status Indicator */}
//         <div className="flex items-center justify-center mb-12 bg-white rounded-full py-3 px-6 shadow-md max-w-md mx-auto">
//           <span className="h-3 w-3 bg-green-500 rounded-full mr-2 animate-pulse"></span>
//           <p className="text-slate-600 flex items-center">
//             <span className="font-medium mr-1 cliffs">{t('support_status')}</span> •
//             <Clock className="w-4 h-4 mx-2 text-slate-400" />
//             <span className="text-slate-500">{t('avg_response_time')}</span>
//           </p>
//         </div>

//         {/* Quick Help Section */}
//         <div className="bg-white rounded-xl shadow-md p-8 mb-12 border border-slate-200">
//           <div className="flex items-center mb-6">
//             <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center mr-4">
//               <Search className="w-5 h-5 text-sky-500" />
//             </div>
//             <h2 className="text-2xl font-semibold text-slate-800">
//               {t('frequently_asked_questions')}
//             </h2>
//           </div>

//           <div className="space-y-4">
//             {faqItems.map((item, index) => (
//               <div key={index} className="border-b border-slate-200 pb-4">
//                 <button
//                   className="w-full text-left flex items-center justify-between py-3 focus:outline-none group"
//                   onClick={() => toggleFaqItem(index)}
//                 >
//                   <span className="text-lg font-medium text-slate-700 group-hover:text-sky-600 transition-colors duration-200">
//                     {item.question}
//                   </span>
//                   <span className={`transform transition-transform duration-200 ${item.isOpen ? 'rotate-180' : ''}`}>
//                     <ChevronUp className="w-5 h-5 text-slate-400" />
//                   </span>
//                 </button>

//                 {item.isOpen && (
//                   <div className="mt-2 pl-4 pr-2 pb-2 text-slate-600 bg-slate-50 rounded-lg p-4 animate-fadeIn">
//                     {item.answer}
//                   </div>
//                 )}
//               </div>
//             ))}
//           </div>
//         </div>

//         {/* Ticket System */}
//         {hasCompany && (
//           <div id="ticket-system" className="mb-12">
//             <TicketSystem setModal={setModal} />
//           </div>
//         )}

//         {/* Tutorials Section */}
//         {hasCompany && (
//           <div className="mb-12">
//             <TutorialVideoSection />
//           </div>
//         )}

//         {/* Contact Section */}
//         <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
//           <div className="bg-white rounded-xl shadow-md p-8 border border-slate-200">
//             <div className="flex items-center mb-6">
//               <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center mr-4">
//                 <MessageCircle className="w-5 h-5 text-sky-500" />
//               </div>
//               <h2 className="text-2xl font-semibold text-slate-800">
//                 {t('contact_us_directly')}
//               </h2>
//             </div>
//             <div className="space-y-4">
//               <div className="p-4 bg-slate-50 rounded-lg flex items-center">
//                 <div className="bg-white p-2 rounded-full mr-4 shadow-sm">
//                   <svg className="w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                     <path
//                       strokeLinecap="round"
//                       strokeLinejoin="round"
//                       strokeWidth={2}
//                       d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
//                     />
//                   </svg>
//                 </div>
//                 <div>
//                   <p className="text-sm text-slate-500">{t('phone_support')}</p>
//                   <p className="text-slate-700 font-medium">+237 .. .. .. ..</p>
//                 </div>
//               </div>
//               <div className="p-4 bg-slate-50 rounded-lg flex items-center">
//                 <div className="bg-white p-2 rounded-full mr-4 shadow-sm">
//                   <svg className="w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                     <path
//                       strokeLinecap="round"
//                       strokeLinejoin="round"
//                       strokeWidth={2}
//                       d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
//                     />
//                   </svg>
//                 </div>
//                 <div>
//                   <p className="text-sm text-slate-500">{t('email_support')}</p>
//                   <p className="text-slate-700 font-medium">support@otenti.com</p>
//                 </div>
//               </div>
//               {/* Afficher le bouton Live Chat uniquement si l'utilisateur a une company */}
//               {hasCompany && (
//                 <button
//                   onClick={scrollToTicketSystem}
//                   className="w-full bg-rose-500 hover:bg-rose-600 text-white py-3 px-6 rounded-lg transition-all duration-200 transform hover:-translate-y-1 shadow-lg shadow-rose-500/20 flex items-center justify-center mt-6"
//                 >
//                   <MessageCircle className="w-5 h-5 mr-2" />
//                   {t('start_live_chat_now')}
//                 </button>
//               )}
//             </div>
//           </div>

//           <div className="bg-gradient-to-br from-slate-800 to-slate-700 rounded-xl shadow-md p-8 text-white relative overflow-hidden">
//             <div className="absolute top-0 right-0 w-64 h-64 bg-sky-400 rounded-full -translate-y-1/4 translate-x-1/4 opacity-10"></div>
//             <div className="absolute bottom-0 left-0 w-64 h-64 bg-rose-400 rounded-full translate-y-1/4 -translate-x-1/4 opacity-10"></div>
//             <div className="relative z-10">
//               <h2 className="text-2xl font-semibold mb-6">{t('support_hours')}</h2>
//               <div className="space-y-4 mb-8">
//                 <div className="flex justify-between">
//                   <span>{t('monday_friday')}</span>
//                   <span className="font-medium">{t('monday_friday_hours')}</span>
//                 </div>
//                 <div className="flex justify-between">
//                   <span>{t('saturday')}</span>
//                   <span className="font-medium">{t('saturday_hours')}</span>
//                 </div>
//                 <div className="flex justify-between">
//                   <span>{t('sunday')}</span>
//                   <span className="font-medium">{t('sunday_hours')}</span>
//                 </div>
//               </div>
//               <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
//                 <h3 className="text-lg font-medium mb-2">{t('emergency_support')}</h3>
//                 <p className="text-slate-300 mb-4">{t('emergency_support_description')}</p>
//                 <Link to="/ Huntington Beach " className="text-sky-300 hover:text-sky-200 flex items-center">
//                   {t('learn_premium_support')}
//                   <svg className="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                     <path
//                       strokeLinecap="round"
//                       strokeLinejoin="round"
//                       strokeWidth={2}
//                       d="M14 5l7 7m0 0l-7 7m7-7H3"
//                     />
//                   </svg>
//                 </Link>
//               </div>
//             </div>
//           </div>
//         </div>

//         {/* Modal for Success/Error Messages */}
//         {modal.isOpen && (
//           <div
//             className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 p-4"
//             style={{
//               position: 'fixed',
//               top: 0,
//               left: 0,
//               right: 0,
//               bottom: 0,
//               zIndex: 100,
//               backgroundColor: 'rgba(0,0,0,0.5)',
//               display: 'flex',
//               alignItems: 'center',
//               justifyContent: 'center',
//             }}
//             onClick={closeModal}
//           >
//             <div
//               className="bg-white rounded-xl shadow-2xl w-full max-w-md relative"
//               onClick={(e) => e.stopPropagation()}
//               style={{
//                 maxHeight: '90vh',
//                 overflowY: 'auto',
//               }}
//             >
//               {/* Modal Header */}
//               <div
//                 className="p-6 pb-0 flex justify-between items-center"
//                 style={{ color: COLORS.darkText }}
//               >
//                 <h2 className="text-xl font-bold flex items-center">
//                   {modal.type === 'success' ? (
//                     <CheckCircle className="mr-2" size={24} style={{ color: COLORS.success }} />
//                   ) : (
//                     <AlertCircle className="mr-2" size={24} style={{ color: COLORS.danger }} />
//                   )}
//                   {modal.title}
//                 </h2>
//                 <button
//                   onClick={closeModal}
//                   className="text-gray-500 hover:text-gray-700 transition-colors"
//                   aria-label={t('close')}
//                 >
//                   <X size={24} />
//                 </button>
//               </div>

//               {/* Modal Content */}
//               <div className="p-6">
//                 <div
//                   className="p-3 rounded-lg flex items-center"
//                   style={{
//                     backgroundColor: modal.type === 'success' ? `${COLORS.success}10` : `${COLORS.danger}10`,
//                     color: modal.type === 'success' ? COLORS.success : COLORS.danger,
//                   }}
//                 >
//                   {modal.type === 'success' ? (
//                     <CheckCircle className="mr-2" size={20} />
//                   ) : (
//                     <AlertCircle className="mr-2" size={20} />
//                   )}
//                   <p className="text-sm">{modal.message}</p>
//                 </div>
//               </div>

//               {/* Modal Footer */}
//               <div className="p-6 pt-0 flex justify-end">
//                 <button
//                   onClick={closeModal}
//                   className="px-4 py-2 bg-opacity-90 text-white rounded-lg transition-colors"
//                   style={{ backgroundColor: COLORS.primary }}
//                 >
//                   {t('close')}
//                 </button>
//               </div>
//             </div>
//           </div>
//         )}

//         {/* Back to Top Button */}
//         <button
//           onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
//           className="fixed bottom-8 right-8 bg-sky-500 text-white p-3 rounded-full shadow-lg hover:bg-sky-600 transition-colors duration-300 z-50"
//         >
//           <ChevronUp className="h-5 w-5" />
//         </button>

//         <Footer />

//         {/* Add animations keyframes */}
//         <style>{`
//           @keyframes fadeIn {
//             from { opacity: 0; transform: translateY(10px); }
//             to { opacity: 1; transform: translateY(0); }
//           }
//           .animate-fadeIn {
//             animation: fadeIn 0.3s ease-out forwards;
//           }
//         `}</style>
//       </div>
//     </div>
//   );
// };

// export default Support;
